import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { 
  MapPin, Phone, Mail, Clock, Send, CheckCircle2, 
  ChevronDown, Car, Route, ArrowRight,
  Plus, Minus, MessageCircle, Loader2, Star, ChevronUp
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// FAQ Data
const faqData = [
  {
    question: "How do I book a transfer?",
    answer: "Booking is simple! Browse our routes, select your pickup and drop-off locations, choose your vehicle type, and fill in your travel details. You'll receive a confirmation email with all the details. You can also contact us directly via phone or email for assistance."
  },
  {
    question: "What is your cancellation policy?",
    answer: "We offer free cancellation up to 24 hours before your scheduled pickup time. Cancellations made within 24 hours may be subject to a fee. For special circumstances, please contact us directly and we'll do our best to accommodate you."
  },
  {
    question: "Do you provide child/baby seats?",
    answer: "Yes! We provide child seats and baby seats free of charge upon request. Please mention the ages of your children when booking so we can prepare the appropriate seats for your journey."
  },
  {
    question: "Are your prices fixed or metered?",
    answer: "All our transfer prices are fixed and agreed upon at the time of booking. There are no hidden fees or surprise charges. The price you see is the price you pay, regardless of traffic conditions or route variations."
  },
  {
    question: "Do you track flight delays?",
    answer: "Absolutely! We monitor all incoming flights in real-time. If your flight is delayed, our driver will adjust the pickup time accordingly at no extra charge. You'll never have to worry about delays affecting your transfer."
  },
  {
    question: "How far in advance should I book?",
    answer: "We recommend booking at least 24-48 hours in advance to ensure availability, especially during peak season (June-September). However, we also accommodate last-minute bookings when possible. Contact us for same-day requests."
  },
  {
    question: "What payment methods do you accept?",
    answer: "We accept cash (Euros), credit cards (Visa, Mastercard, American Express), and bank transfers. Payment can be made in advance online or directly to the driver after your transfer."
  },
  {
    question: "Can I modify my booking?",
    answer: "Yes, you can modify your booking up to 12 hours before your scheduled pickup time. Changes to date, time, or pickup location can be made by contacting us via email or phone. We'll confirm any changes promptly."
  }
];

// Testimonials data
const testimonials = [
  {
    id: 1,
    name: 'Sarah Johnson',
    location: 'London, UK',
    rating: 5,
    text: 'Excellent service! The driver was waiting for us at the airport with a name sign. Very professional and the car was spotless. Highly recommend!',
  },
  {
    id: 2,
    name: 'Michael Schmidt',
    location: 'Munich, Germany',
    rating: 5,
    text: 'We used Kalamata Airport Taxi for our family trip to Costa Navarino. The minivan was perfect for our group. Great value!',
  },
  {
    id: 3,
    name: 'Emma Rossi',
    location: 'Milan, Italy',
    rating: 5,
    text: 'Punctual, friendly, and very helpful. Our driver gave us great tips about the area. The booking process was simple.',
  },
];

const ContactPage = () => {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [openFaq, setOpenFaq] = useState(null);
  const [showSubjectDropdown, setShowSubjectDropdown] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: 'general',
    message: ''
  });

  const subjectOptions = [
    { value: 'general', label: 'General Inquiry' },
    { value: 'transfer', label: 'Transfer Booking' },
    { value: 'corporate', label: 'Group/Corporate' },
    { value: 'other', label: 'Other' },
  ];

  const selectedSubjectLabel = subjectOptions.find(opt => opt.value === formData.subject)?.label || 'Select subject';

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Close dropdown on outside click
  React.useEffect(() => {
    const handleClickOutside = () => setShowSubjectDropdown(false);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_BASE}/api/contact/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: `[${formData.subject.toUpperCase()}] ${formData.message}`,
          form_type: 'general'
        })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to send message');
      }
      
      setFormSubmitted(true);
      setTimeout(() => {
        setFormSubmitted(false);
        setFormData({ name: '', email: '', phone: '', subject: 'general', message: '' });
      }, 5000);
    } catch (err) {
      setError(err.message || 'Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Set document meta tags
  useDocumentMeta({
    title: 'Contact Kalamata Airport Taxi | 24/7 Support | Messenia Transfers',
    description: 'Contact Kalamata Airport Taxi for airport transfers in Messenia. 24/7 support via phone, email or WhatsApp. Quick response guaranteed.',
    canonical: 'https://kalamataairporttaxi.com/contact',
    ogTitle: 'Contact Kalamata Airport Taxi | 24/7 Support',
    ogDescription: 'Contact Kalamata Airport Taxi for airport transfers in Messenia. 24/7 support via phone, email or WhatsApp.',
    ogUrl: 'https://kalamataairporttaxi.com/contact'
  });

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative min-h-[50vh] flex items-center bg-[#2d232e] overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
              backgroundSize: '40px 40px'
            }} />
          </div>
          
          {/* Decorative Elements */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#36827f]/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#36827f]/10 rounded-full blur-3xl" />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative pt-32 pb-20 w-full">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm text-white/50 mb-8">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <ChevronDown className="h-4 w-4 rotate-[-90deg]" />
              <span className="text-[#36827f]">Contact</span>
            </nav>

            <div className="text-center max-w-3xl mx-auto">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-6">
                <Mail className="h-4 w-4 text-[#36827f]" />
                <span className="text-sm font-medium text-white/90">Get in Touch</span>
              </div>

              <h1 className="text-4xl lg:text-5xl font-bold text-white mb-6">
                We're Here to <span className="text-[#36827f]">Help</span>
              </h1>
              
              <p className="text-lg text-white/60">
                Have questions about transfers? Need a custom quote? 
                Our team is ready to assist you 24/7.
              </p>
            </div>
          </div>
        </section>

        {/* Contact Info + Form Section */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              {/* Left Column - Contact Details */}
              <div>
                <span className="inline-block px-4 py-1.5 rounded-full bg-[#36827f]/10 text-[#36827f] text-sm font-semibold mb-4">
                  CONTACT INFORMATION
                </span>
                <h2 className="text-3xl lg:text-4xl font-bold text-[#2d232e] mb-8">
                  Let's Connect
                </h2>

                <div className="space-y-6 mb-10">
                  {/* Phone */}
                  <a href="tel:+306932421170" className="flex items-start gap-4 group">
                    <div className="w-14 h-14 rounded-xl bg-[#36827f]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#36827f] transition">
                      <Phone className="h-6 w-6 text-[#36827f] group-hover:text-white transition" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#2d232e] mb-1">Phone (24/7)</h3>
                      <p className="text-gray-600 text-lg">+30 693 242 1170</p>
                    </div>
                  </a>

                  {/* WhatsApp */}
                  <a href="https://wa.me/306932421170" target="_blank" rel="noopener noreferrer" className="flex items-start gap-4 group">
                    <div className="w-14 h-14 rounded-xl bg-[#25D366]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#25D366] transition">
                      <MessageCircle className="h-6 w-6 text-[#25D366] group-hover:text-white transition" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#2d232e] mb-1">WhatsApp</h3>
                      <p className="text-gray-600 text-lg">+30 693 242 1170</p>
                    </div>
                  </a>

                  {/* Email */}
                  <a href="mailto:info@kalamataairporttaxi.com" className="flex items-start gap-4 group">
                    <div className="w-14 h-14 rounded-xl bg-[#36827f]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#36827f] transition">
                      <Mail className="h-6 w-6 text-[#36827f] group-hover:text-white transition" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#2d232e] mb-1">Email</h3>
                      <p className="text-gray-600 text-lg">info@kalamataairporttaxi.com</p>
                    </div>
                  </a>

                  {/* Hours */}
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-xl bg-[#36827f]/10 flex items-center justify-center flex-shrink-0">
                      <Clock className="h-6 w-6 text-[#36827f]" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#2d232e] mb-1">Service Hours</h3>
                      <p className="text-gray-600 text-lg">24/7 - Available anytime</p>
                      <p className="text-sm text-gray-500">We're always here for you</p>
                    </div>
                  </div>

                  {/* Location */}
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-xl bg-[#36827f]/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="h-6 w-6 text-[#36827f]" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#2d232e] mb-1">Location</h3>
                      <p className="text-gray-600 text-lg">Kalamata Airport, Greece</p>
                    </div>
                  </div>
                </div>

                {/* Quick Response Promise */}
                <div className="bg-gradient-to-br from-[#36827f]/5 to-[#36827f]/10 rounded-2xl p-6 border border-[#36827f]/20">
                  <div className="flex items-center gap-3 mb-3">
                    <CheckCircle2 className="h-6 w-6 text-[#36827f]" />
                    <h3 className="font-bold text-[#2d232e]">Quick Response Promise</h3>
                  </div>
                  <p className="text-gray-600">
                    We respond to all inquiries within 2 hours during business hours, 
                    and within 12 hours for overnight messages.
                  </p>
                </div>
              </div>

              {/* Right Column - Contact Form */}
              <div>
                {formSubmitted ? (
                  <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-xl text-center">
                    <div className="w-20 h-20 rounded-full bg-[#36827f]/10 flex items-center justify-center mx-auto mb-6">
                      <CheckCircle2 className="h-10 w-10 text-[#36827f]" />
                    </div>
                    <h3 className="text-2xl font-bold text-[#2d232e] mb-3">Message Sent!</h3>
                    <p className="text-gray-600 mb-6">
                      Thank you for reaching out. We'll get back to you as soon as possible.
                    </p>
                    <Button
                      onClick={() => setFormSubmitted(false)}
                      variant="outline"
                      className="border-gray-300 hover:border-[#36827f] hover:text-[#36827f]"
                      data-testid="send-another-btn"
                    >
                      Send Another Message
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="bg-gray-50 rounded-2xl p-8">
                    <h3 className="text-2xl font-bold text-[#2d232e] mb-6">Send us a Message</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Your Name *</label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:border-[#36827f] focus:ring-2 focus:ring-[#36827f]/20 transition-all"
                          placeholder="John Smith"
                          data-testid="contact-name-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:border-[#36827f] focus:ring-2 focus:ring-[#36827f]/20 transition-all"
                          placeholder="john@example.com"
                          data-testid="contact-email-input"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:border-[#36827f] focus:ring-2 focus:ring-[#36827f]/20 transition-all"
                          placeholder="+30 XXX XXX XXXX"
                          data-testid="contact-phone-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Subject *</label>
                        <div className="relative" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            onClick={() => setShowSubjectDropdown(!showSubjectDropdown)}
                            className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:border-[#36827f] focus:ring-2 focus:ring-[#36827f]/20 transition-all text-left flex items-center justify-between text-gray-900"
                            data-testid="contact-subject-select"
                          >
                            <span>{selectedSubjectLabel}</span>
                            <div className={`bg-[#36827f]/10 rounded-lg p-1.5 transition-transform ${showSubjectDropdown ? 'rotate-180' : ''}`}>
                              <ChevronDown className="h-4 w-4 text-[#36827f]" />
                            </div>
                          </button>
                          {showSubjectDropdown && (
                            <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden">
                              {subjectOptions.map((option) => (
                                <div
                                  key={option.value}
                                  onClick={() => {
                                    setFormData({ ...formData, subject: option.value });
                                    setShowSubjectDropdown(false);
                                  }}
                                  className={`px-4 py-3 cursor-pointer transition-colors ${
                                    formData.subject === option.value 
                                      ? 'bg-[#36827f] text-white' 
                                      : 'hover:bg-[#36827f]/10 text-gray-900'
                                  }`}
                                >
                                  {option.label}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Your Message *</label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:border-[#36827f] focus:ring-2 focus:ring-[#36827f]/20 transition-all resize-none"
                        placeholder="Tell us how we can help you..."
                        data-testid="contact-message-input"
                      />
                    </div>

                    {error && (
                      <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                        {error}
                      </div>
                    )}

                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-[#36827f] hover:bg-[#2d6f6c] text-white py-5 text-lg font-bold rounded-xl shadow-lg shadow-[#36827f]/20 hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      data-testid="contact-submit-btn"
                    >
                      {loading ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <Send className="h-5 w-5" />
                      )}
                      {loading ? 'Sending...' : 'Send Message'}
                    </Button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Quick Links Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 rounded-full bg-[#36827f]/10 text-[#36827f] text-sm font-semibold mb-4">
                QUICK ACTIONS
              </span>
              <h2 className="text-3xl lg:text-4xl font-bold text-[#2d232e] mb-4">
                Ready to Book?
              </h2>
              <p className="text-gray-600 text-lg">Jump right in and plan your transfer</p>
            </div>

            <div className="max-w-xl mx-auto">
              {/* Book Transfer */}
              <Link
                to="/routes"
                className="group bg-white rounded-2xl p-8 border border-gray-100 hover:border-[#36827f]/30 hover:shadow-xl hover:shadow-[#36827f]/10 transition-all duration-300 hover:-translate-y-1 flex flex-col items-center text-center"
                data-testid="quick-link-transfers"
              >
                <div className="w-16 h-16 rounded-xl bg-[#36827f]/10 flex items-center justify-center mb-6 group-hover:bg-[#36827f] transition-colors">
                  <Route className="h-8 w-8 text-[#36827f] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-bold text-[#2d232e] mb-2 group-hover:text-[#36827f] transition-colors">
                  Browse All Routes & Book
                </h3>
                <p className="text-gray-600 mb-4">
                  Airport transfers to Costa Navarino, Pylos, Stoupa, Kardamyli, and all Messenia destinations with fixed prices.
                </p>
                <span className="text-[#36827f] font-semibold flex items-center gap-2 group-hover:gap-3 transition-all">
                  View Routes <ArrowRight className="h-4 w-4" />
                </span>
              </Link>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="py-24 bg-gradient-to-br from-[#2d232e] via-[#3d333e] to-[#2d232e] relative overflow-hidden">
          <div className="absolute top-0 left-0 w-72 h-72 bg-[#36827f]/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#36827f]/10 rounded-full blur-3xl" />
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="text-center mb-16">
              <span className="inline-block px-4 py-1.5 rounded-full bg-[#36827f]/20 text-[#36827f] text-sm font-semibold mb-4 border border-[#36827f]/30">
                TESTIMONIALS
              </span>
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                What Our Customers Say
              </h2>
              <p className="text-white/60 max-w-2xl mx-auto text-lg">
                Trusted by travelers from around the world
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((testimonial, index) => (
                <div 
                  key={testimonial.id}
                  className={`rounded-2xl p-8 transform hover:-translate-y-2 transition-all duration-300 ${
                    index === 1 
                      ? 'bg-gradient-to-br from-[#36827f] to-[#2d6f6c] shadow-xl shadow-[#36827f]/25' 
                      : 'bg-white/10 backdrop-blur-sm border border-white/10 hover:bg-white/15'
                  }`}
                >
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  
                  <p className={`mb-6 leading-relaxed ${index === 1 ? 'text-white/90' : 'text-white/70'}`}>
                    "{testimonial.text}"
                  </p>
                  
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold ${
                      index === 1 
                        ? 'bg-white text-[#36827f]' 
                        : 'bg-[#36827f] text-white'
                    }`}>
                      {testimonial.name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div>
                      <p className="font-bold text-white">{testimonial.name}</p>
                      <p className={`text-sm ${index === 1 ? 'text-white/70' : 'text-white/50'}`}>{testimonial.location}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-24 bg-gray-50">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="inline-block px-4 py-1.5 rounded-full bg-[#36827f]/10 text-[#36827f] text-sm font-semibold mb-4">
                FAQ
              </span>
              <h2 className="text-3xl lg:text-4xl font-bold text-[#2d232e] mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-gray-600 text-lg">
                Find answers to common questions about our services
              </p>
            </div>

            <div className="space-y-4">
              {faqData.map((faq, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-xl shadow-sm overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition"
                    data-testid={`faq-toggle-${index}`}
                  >
                    <span className="font-semibold text-[#2d232e] pr-4">{faq.question}</span>
                    {openFaq === index ? (
                      <ChevronUp className="w-5 h-5 text-[#36827f] flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    )}
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-6">
                      <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Still Have Questions */}
            <div className="mt-12 text-center bg-white rounded-2xl p-8 shadow-sm">
              <h3 className="text-xl font-bold text-[#2d232e] mb-3">Still have questions?</h3>
              <p className="text-gray-600 mb-6">
                Can't find the answer you're looking for? Please contact our friendly team.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a
                  href="tel:+306932421170"
                  className="inline-flex items-center gap-2 bg-[#2d232e] hover:bg-[#3d333e] text-white px-6 py-3 rounded-xl font-semibold transition-colors"
                >
                  <Phone className="h-5 w-5" />
                  Call Us
                </a>
                <a
                  href="mailto:info@kalamataairporttaxi.com"
                  className="inline-flex items-center gap-2 bg-[#36827f] hover:bg-[#2d6f6c] text-white px-6 py-3 rounded-xl font-semibold transition-colors"
                >
                  <Mail className="h-5 w-5" />
                  Email Us
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;
